import { RaceId, raceTable, SegmentId } from "../domain/event";
import { executeSupabaseQuery, useQuery } from "../utils/supabase/useQuery";
import {
    ResultRow,
    resultsTable,
    resultsWithCategoryTitleView,
} from "../domain/result";
import { omit, pick, someOrThrow } from "../utils/fp";
import { column, columns, join } from "../utils/pg-ts/pg-ts";
import * as t from "io-ts";
import { flow, pipe } from "fp-ts/function";
import { array, number, option, readonlyArray, taskEither } from "fp-ts";
import { SupabaseClient } from "@supabase/supabase-js";
import { MutatorCallback } from "swr";
import { contramap } from "fp-ts/Ord";
import { QueryError } from "../utils/supabase/QueryState";
import { TaskEither } from "fp-ts/TaskEither";
import { clubsTable } from "../domain/club";
import { nullable, SearchTerm } from "../domain/common";
import { OrganizationId } from "../domain/organization";

const resultWithCategoryTitleCols = resultsWithCategoryTitleView.columns;
const raceCols = pipe(raceTable.columns, pick("title"));
const raceAlias = "race";
const clubAlias = "clubs"; // TODO once the schizma club, club_id is solved, rename to club
const clubCols = pipe(clubsTable.columns, pick("id", "title"));

const resultClubCodec = t.type(clubCols, clubAlias);
export type ResultClub = t.TypeOf<typeof resultClubCodec>;

const resultWithCategoryTitleCodec = t.type(
    {
        ...resultWithCategoryTitleCols,
        [raceAlias]: t.type(raceCols, raceAlias),
        [clubAlias]: nullable(resultClubCodec),
    },
    "ResultWithCategoryTitle"
);
export type ResultWithCategoryTitle = t.TypeOf<
    typeof resultWithCategoryTitleCodec
>;
export type NonNullableCategoryTitle = NonNullable<
    ResultWithCategoryTitle["category_title"]
>;

export const useResultsWithCategoryTitle = (
    raceIds: Array<RaceId>,
    refreshInterval?: number
) => {
    const shouldSkip = array.isEmpty(raceIds);

    return useQuery(
        pipe(
            raceIds,
            array.sort(number.Ord),
            (ids) => `results_${ids.join("_")}`
        ),
        (supabase) =>
            supabase
                .from(resultsWithCategoryTitleView.name)
                .select(
                    columns(
                        resultWithCategoryTitleCols,
                        join(raceTable, raceCols, raceAlias),
                        join(clubsTable, clubCols, clubAlias)
                    )
                )
                .in(column(resultsTable, "race_id"), raceIds),
        t.array(resultWithCategoryTitleCodec),
        { refreshInterval },
        shouldSkip
    );
};

export type ResultsWithCategoryTitleQuery = ReturnType<
    typeof useResultsWithCategoryTitle
>;

const segmentResultRaceCols = pipe(
    raceTable.columns,
    pick("title", "start_time")
);

const segmentResultCodec = t.type(
    {
        ...resultWithCategoryTitleCols,
        [raceAlias]: t.type(segmentResultRaceCols),
    },
    "SegmentResult"
);
export type SegmentResult = t.TypeOf<typeof segmentResultCodec>;
export const useSegmentResults = (segmentId: SegmentId) => {
    return useQuery(
        `segment_results_${segmentId}`,
        (supabase) =>
            supabase
                .rpc("segment_result", {
                    segment_id: segmentId,
                })
                .select(
                    columns(
                        resultWithCategoryTitleCols,
                        join(raceTable, segmentResultRaceCols, raceAlias)
                    )
                ),
        // .neq(column(resultsWithCategoryTitleView, "finish_time"), null),
        t.array(segmentResultCodec)
    );
};

const resultUpdateOutputCodec = t.type(
    resultsTable.columns,
    "ResultUpdateOutput"
);
export type ResultUpdateOutput = t.TypeOf<typeof resultUpdateOutputCodec>;

export const resulUpdateInputCodec = t.intersection(
    [
        // all fields are optional, except id
        pipe(resultsTable.columns, pick("id"), (cols) =>
            t.type(cols, "required")
        ),
        pipe(omit(resultsTable.columns, "id"), (cols) =>
            t.partial(cols, "optional")
        ),
    ],
    "ResultUpdateInput"
);
export type ResultUpdateInput = t.TypeOf<typeof resulUpdateInputCodec>;

export const resultsBulkUpdate =
    (supabase: SupabaseClient) =>
    (
        rowsToUpdate: Array<ResultUpdateInput>
    ): TaskEither<QueryError, Array<ResultUpdateOutput>> => {
        return pipe(
            rowsToUpdate,
            array.map(
                (row): TaskEither<QueryError, ResultUpdateOutput> =>
                    executeSupabaseQuery(
                        "resultBulkUpdate",
                        resultUpdateOutputCodec,
                        (supabase) =>
                            supabase
                                .from(resultsTable.name)
                                .update(row) // TODO use bulk update once it arrives in supabase https://github.com/supabase/postgrest-js/issues/174
                                .eq(column(resultsTable, "id"), row.id)
                                .single(),
                        supabase
                    )
            ),
            taskEither.sequenceArray,
            taskEither.map(readonlyArray.toArray)
        );
    };

export const mutateResults =
    (
        updatedResults: Array<ResultUpdateOutput>
    ): MutatorCallback<Array<ResultWithCategoryTitle>> =>
    (currentData) => {
        return pipe(
            currentData,
            option.fromNullable,
            option.map(
                flow(
                    sortResultsById,
                    array.zip(sortResultsById(updatedResults)),
                    array.map(([origRes, updatedRes]) => {
                        // order matters
                        if (origRes.id != updatedRes.id) {
                            throw new Error(
                                `tried to update wrong results: "${origRes.id}" to "${updatedRes.id}"`
                            );
                        }
                        return { ...origRes, ...updatedRes };
                    })
                )
            ),
            someOrThrow
        );
    };

interface WithResultId {
    id: ResultRow["id"];
}
const ordByResultId = pipe(
    number.Ord,
    contramap((r: WithResultId) => r.id)
);

const sortResultsById = array.sort(ordByResultId);

export const clubSearch =
    (client: SupabaseClient, organizationId: OrganizationId) =>
    (searchTerm: SearchTerm) => {
        return executeSupabaseQuery(
            `clubSearch_${searchTerm}`,
            t.array(resultClubCodec),
            (client) =>
                client
                    .from(clubsTable.name)
                    .select(columns(clubCols))
                    .eq(column(clubsTable, "organization_id"), organizationId)
                    .ilike(column(clubsTable, "title"), `%${searchTerm}%`),
            client
        );
    };
